import React from 'react';
import {
    Card,
    CardContent,
    Divider,
    CardHeader,
    TableCell,
    Table,
    TableBody,
    TableRow,
    makeStyles,
    Theme,
    Fade,
    Box,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

/**
 * Shape persisted on the lead as `webhookData` for Incadea-sourced leads.
 * All values are strings (normalized on the backend).
 */
export interface IIncadeaWebhookData {
    entryNo?: string;
    leadBufferNo?: string;
    opportunityId?: string;
    campaignName?: string;
    modelGroup?: string;
}

interface IIncadeaInformationProps {
    webhookData: IIncadeaWebhookData;
    loading: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
    margin: {
        marginBottom: '1em',
    },
    labelCell: {
        fontWeight: 500,
        width: '40%',
        backgroundColor: theme.palette.background.default,
    },
    valueCell: {
        width: '60%',
    },
    sectionTitle: {
        fontWeight: 600,
        backgroundColor: theme.palette.action.hover,
        padding: theme.spacing(1),
    },
}));

const displayString = (value: string | undefined): string =>
    value != null && String(value).trim() !== '' ? String(value) : '- - -';

export const IncadeaInformation = ({
    webhookData,
    loading,
}: IIncadeaInformationProps) => {
    const { t } = useTranslation();
    const classes = useStyles();

    return (
        <Card>
            <CardHeader title={t('IncadeaInformation.Title')} />
            <Divider />
            <CardContent style={{ padding: 0 }}>
                <Table className={classes.margin}>
                    <TableBody>
                        <TableRow>
                            <TableCell className={classes.labelCell}>
                                {t('IncadeaInformation.EntryNo')}
                            </TableCell>
                            <Fade in={!loading} timeout={600}>
                                <TableCell className={classes.valueCell}>
                                    {displayString(webhookData.entryNo)}
                                </TableCell>
                            </Fade>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>
                                {t('IncadeaInformation.LeadBufferNo')}
                            </TableCell>
                            <Fade in={!loading} timeout={600}>
                                <TableCell className={classes.valueCell}>
                                    {displayString(
                                        webhookData.leadBufferNo
                                    )}
                                </TableCell>
                            </Fade>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>
                                {t('IncadeaInformation.OpportunityId')}
                            </TableCell>
                            <Fade in={!loading} timeout={600}>
                                <TableCell className={classes.valueCell}>
                                    {displayString(
                                        webhookData.opportunityId
                                    )}
                                </TableCell>
                            </Fade>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>
                                {t('IncadeaInformation.CampaignName')}
                            </TableCell>
                            <Fade in={!loading} timeout={600}>
                                <TableCell className={classes.valueCell}>
                                    {displayString(
                                        webhookData.campaignName
                                    )}
                                </TableCell>
                            </Fade>
                        </TableRow>
                        <TableRow>
                            <TableCell className={classes.labelCell}>
                                {t('IncadeaInformation.ModelGroup')}
                            </TableCell>
                            <Fade in={!loading} timeout={600}>
                                <TableCell className={classes.valueCell}>
                                    {displayString(
                                        webhookData.modelGroup
                                    )}
                                </TableCell>
                            </Fade>
                        </TableRow>
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};
